/**
 * AssessmentSummaryCard — at-a-glance assessment progress for an imported
 * document, shown on the "Document details" tab.
 *
 * Overall: how many visible blocks carry a saved note code (see
 * evaluationProgress — hidden blocks/columns are left out of the total).
 *
 * Departments: how many department-assigned fragments already carry a comment (a
 * discussion thread with at least one message) and renders an overall
 * completeness ring plus a per-department breakdown. Self-contained: it loads
 * the discussions for this document (the details tab can open without visiting
 * the content tab, which is the other place discussions get loaded).
 */

import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    CircularProgress,
    Paper,
    Typography,
} from "@mui/material";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import { Localized } from "@fluent/react";

import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { selectAllDeptAssignments } from "../importedDocDepartmentsSlice";
import { selectHiddenParts } from "../importedDocHiddenPartsSlice";
import { ImportedDocumentElement } from "../importedDocumentsApi";
import { evaluableBlockKeys, evaluationProgress } from "../ast/evaluationProgress";
import { selectAllUrsOfferNotes } from "../../../ursOffer/ursOfferNotesSlice";
import { loadDiscussions, selectThreadSummaries } from "../discussionsSlice";
import {
    CompanyDepartmentDetails,
    loadCompanyDepartments,
} from "../../../administration/companyDepartments/companyDepartmentsSlice";

interface Props {
    docId: number;
    docGuid: string;
    elements: ImportedDocumentElement[];
}

interface DeptRow {
    deptId: number;
    total: number;
    commented: number;
}

type Accent = "success" | "primary";

const ProgressRing = ({ pct, accent }: { pct: number; accent: Accent }) => (
    <Box sx={{ position: "relative", display: "inline-flex" }}>
        {/* Track ring underneath the determinate progress. */}
        <CircularProgress
            variant="determinate"
            value={100}
            size={64}
            thickness={4}
            sx={{ color: "var(--gray-200)" }}
        />
        <CircularProgress
            variant="determinate"
            value={pct}
            size={64}
            thickness={4}
            color={accent}
            sx={{ position: "absolute", left: 0, strokeLinecap: "round" }}
        />
        <Box
            sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }}
        >
            <Typography variant="caption" fontWeight={700} color={`${accent}.main`}>
                {pct}%
            </Typography>
        </Box>
    </Box>
);

export const AssessmentSummaryCard: React.FC<Props> = ({ docId, docGuid, elements }) => {
    const dispatch = useAppDispatch();
    const assignments = useAppSelector(selectAllDeptAssignments);
    const summaries = useAppSelector(selectThreadSummaries);
    const notes = useAppSelector(selectAllUrsOfferNotes);
    const hiddenParts = useAppSelector(selectHiddenParts);
    // The department master list is the source of truth for code/name/colour —
    // the denormalised fields on an assignment can be null (same lookup the
    // NotePanel dept tiles use).
    const [deptById, setDeptById] = useState<Record<number, CompanyDepartmentDetails>>({});

    useEffect(() => {
        if (docGuid) dispatch(loadDiscussions({ relatedObjectId: docGuid }));
    }, [dispatch, docGuid]);

    useEffect(() => {
        dispatch(loadCompanyDepartments())
            .unwrap()
            .then((d) => setDeptById(Object.fromEntries(d.map((x) => [x.id, x]))))
            .catch(console.error);
    }, [dispatch]);

    const { total, commented, byDept } = useMemo(() => {
        const isCommented = (blockKey: string) => !!summaries[blockKey.toLowerCase()];
        const deptMap = new Map<number, DeptRow>();
        let done = 0;
        for (const a of assignments) {
            const row =
                deptMap.get(a.companyDepartmentId) ?? {
                    deptId: a.companyDepartmentId,
                    total: 0,
                    commented: 0,
                };
            row.total += 1;
            if (isCommented(a.blockKey)) {
                row.commented += 1;
                done += 1;
            }
            deptMap.set(a.companyDepartmentId, row);
        }
        return {
            total: assignments.length,
            commented: done,
            byDept: [...deptMap.values()],
        };
    }, [assignments, summaries]);

    const complete = total > 0 && commented === total;

    // Parsing the whole document is the expensive part — keep it off the notes
    // dependency so saving a note only re-counts.
    const blockKeys = useMemo(() => evaluableBlockKeys(elements ?? [], hiddenParts), [elements, hiddenParts]);
    const overall = useMemo(() => evaluationProgress(blockKeys, notes), [blockKeys, notes]);
    const overallPct = overall.total === 0 ? 0 : Math.round((overall.evaluated / overall.total) * 100);
    const overallDone = overall.total > 0 && overall.evaluated === overall.total;

    // Match the NotePanel dept tiles: order by the department's displayOrder.
    const orderedDept = useMemo(
        () =>
            [...byDept].sort(
                (a, b) =>
                    (deptById[a.deptId]?.displayOrder ?? 0) - (deptById[b.deptId]?.displayOrder ?? 0),
            ),
        [byDept, deptById],
    );

    return (
        <Paper elevation={3} sx={{ p: 1.5, mb: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                <AssignmentTurnedInOutlinedIcon fontSize="small" color="action" />
                <Typography variant="subtitle2" fontWeight={700}>
                    <Localized id="imported-doc-assessment-summary">_Assessment summary</Localized>
                </Typography>
            </Box>

            {overall.total > 0 && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                    <ProgressRing pct={overallPct} accent={overallDone ? "success" : "primary"} />
                    <Box>
                        <Typography variant="body2" fontWeight={600}>
                            <Localized
                                id="assessment-summary-overall-progress"
                                vars={{ evaluated: overall.evaluated, total: overall.total }}
                            >
                                {`_${overall.evaluated} of ${overall.total} fragments evaluated`}
                            </Localized>
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {overallDone ? (
                                <Localized id="assessment-summary-overall-complete">
                                    _All fragments have been evaluated.
                                </Localized>
                            ) : (
                                <Localized
                                    id="assessment-summary-overall-remaining"
                                    vars={{ remaining: overall.total - overall.evaluated }}
                                >
                                    {`_${overall.total - overall.evaluated} not yet evaluated`}
                                </Localized>
                            )}
                        </Typography>
                    </Box>
                </Box>
            )}

            {total === 0 ? (
                <Typography variant="body2" color="text.secondary">
                    <Localized id="assessment-summary-empty">
                        _No department assignments yet — assign departments to fragments to start the assessment.
                    </Localized>
                </Typography>
            ) : (
                <>
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" fontWeight={600}>
                            <Localized id="assessment-summary-progress" vars={{ commented, total }}>
                                {`_${commented} of ${total} assignments commented`}
                            </Localized>
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {complete ? (
                                <Localized id="assessment-summary-complete">
                                    _All department assignments have been commented.
                                </Localized>
                            ) : (
                                <Localized id="assessment-summary-remaining" vars={{ remaining: total - commented }}>
                                    {`_${total - commented} still awaiting a comment`}
                                </Localized>
                            )}
                        </Typography>
                    </Box>

                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2.5 }}>
                        {orderedDept.map((d) => {
                        const pct = d.total === 0 ? 0 : Math.round((d.commented / d.total) * 100);
                        const deptDone = d.commented === d.total;
                        const accent: Accent = deptDone ? "success" : "primary";
                        const dept = deptById[d.deptId];
                        return (
                            <Paper
                                key={d.deptId}
                                variant="outlined"
                                title={dept?.name ?? undefined}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.5,
                                    p: 1,
                                    borderRadius: 2,
                                }}
                            >
                                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", alignSelf: "stretch", gap: 0.5 }}>
                                    <Box
                                        sx={{
                                            width: 36,
                                            height: 36,
                                            borderRadius: 2,
                                            flexShrink: 0,
                                            bgcolor: dept?.color || "primary.main",
                                            color: "#fff",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            fontSize: 12,
                                            fontWeight: 700,
                                            boxShadow: 1,
                                        }}
                                    >
                                        {dept?.code ?? "—"}
                                    </Box>
                                    <Typography variant="caption" color="text.secondary">
                                        {d.commented}/{d.total}
                                    </Typography>
                                </Box>

                                <ProgressRing pct={pct} accent={accent} />
                            </Paper>
                        );
                        })}
                    </Box>
                </>
            )}
        </Paper>
    );
};

export default AssessmentSummaryCard;
